"use strict";
// Asset contracts shared by web and mobile: list/profile and concurrency snapshots, linked cost
// and recurring-task reads, work-order history, and document operations. The legacy cost-summary
// route remains unchanged; work-order history is a separate additive all-category mobile view.
Object.defineProperty(exports, "__esModule", { value: true });
exports.fromLabelPayload = exports.toLabelPayload = exports.LABEL_PAYLOAD_PREFIX = exports.resolveAssetConflicts = exports.isAssetConcurrencyConflict = exports.getAssetFieldConflicts = void 0;
const ASSET_CONFLICT_CODE = 'asset_concurrency_conflict';
const DATE_FIELDS = ['installDate', 'warrantyExpiration'];
// Reads the conflicting fields from a failed `upsertAsset` (RTK Query error), or `undefined` when
// the failure is not an overlapping-field conflict.
function getAssetFieldConflicts(error) {
    var _a;
    const data = error === null || error === void 0 ? void 0 : error.data;
    return (data === null || data === void 0 ? void 0 : data.code) === ASSET_CONFLICT_CODE && ((_a = data.conflicts) === null || _a === void 0 ? void 0 : _a.length) ? data.conflicts : undefined;
}
exports.getAssetFieldConflicts = getAssetFieldConflicts;
function isAssetConcurrencyConflict(error) {
    var _a;
    return ((_a = error === null || error === void 0 ? void 0 : error.data) === null || _a === void 0 ? void 0 : _a.code) === ASSET_CONFLICT_CODE;
}
exports.isAssetConcurrencyConflict = isAssetConcurrencyConflict;
// Turns the user's per-field choices into the request that resolves the conflicts. A "theirs"
// field takes the saved value in the draft; every conflicting field takes it in the baseline, so
// the server sees only the choices as changes and keeps merging everything else. Sending the same
// stale `expectedEtag` keeps the server the one place that merges, and any newer change in the
// meantime comes back as a fresh conflict instead of being overwritten.
function resolveAssetConflicts(request, conflicts, choices) {
    const assetView = Object.assign({}, request.assetView);
    const base = Object.assign({}, request.base);
    conflicts.forEach(({ field, currentValue }) => {
        const value = DATE_FIELDS.includes(field) && currentValue === '' ? undefined : currentValue;
        base[field] = value;
        if (choices[field] === 'theirs')
            assetView[field] = value;
    });
    return Object.assign(Object.assign({}, request), { assetView: assetView, base: base });
}
exports.resolveAssetConflicts = resolveAssetConflicts;
// Label payload format (research.md item 12, added 2026-09-18; corrected 2026-09-21): `TRK:`
// followed by the asset's `id`, uppercased. Originally designed assuming `Asset.id` is a GUID
// (hence "strip hyphens, reinsert at 8-4-4-4-12" symmetry) — that assumption was wrong.
// `CreateAssetCommand` (Services/Tickets) always generates ids via `ISequenceService` as
// `{PREFIX}-{NUMBER}` (e.g. `AST-2`), never a GUID. The hyphen there is a meaningful part of the
// id, not GUID punctuation, and QR alphanumeric mode already includes `-` in its character set
// (alongside `0-9`, `A-Z`, `$ % * + . / :`), so there's no space benefit to stripping it — the
// payload is simply the prefix plus the exact id, uppercased. Shared by the web `AssetLabel`
// component (encode) and the mobile `AssetScanScreen` (decode) so a scanned code can be rejected
// as "not a recognized asset" locally, without a network round trip.
exports.LABEL_PAYLOAD_PREFIX = 'TRK:';
function toLabelPayload(assetId) {
    return `${exports.LABEL_PAYLOAD_PREFIX}${assetId.toUpperCase()}`;
}
exports.toLabelPayload = toLabelPayload;
// Returns null for any code that doesn't start with the Trackly prefix, or that has nothing
// after it, so a scanned code can be rejected as unrecognized before ever calling `getAssetById`.
function fromLabelPayload(code) {
    if (!code.startsWith(exports.LABEL_PAYLOAD_PREFIX))
        return null;
    const id = code.slice(exports.LABEL_PAYLOAD_PREFIX.length);
    return id.length > 0 ? id : null;
}
exports.fromLabelPayload = fromLabelPayload;
